import {
  useBudgetSummary,
  useChecklistSummary,
  useEventsSummary,
  useGuestsSummary,
  useSuppliersSummary,
  useVenuePreferencesSummary,
} from '@bridebook/web/lib/ai-planner/hooks/use-wedding-summary-sections';
import type { ResolvedChecklist } from '@bridebook/web/lib/ai-planner/planning/blueprint/types';
import { selectPlannerProfile, selectPlannerWriter } from '@bridebook/web/lib/ai-planner/selectors';
import { useSelector } from '@bridebook/web/lib/utils';
import type { WeddingContextSummary } from '@bridebook/web/lib/ai-planner/wedding-context-summary';
import { getWeddingId } from '@bridebook/web/lib/weddings/selectors';
import { type MutableRefObject, useEffect, useMemo } from 'react';

/**
 * The full planning summary sent to the chat route, built from the section hooks, with its
 * load state. Ready when a profile exists and the checklist and budget sources finished
 * loading. Failed when one of them failed. Coach memory and preferences are optional and
 * never block a send.
 *
 * Not exported on purpose: it subscribes to every wedding slice and query, which suits the
 * request body but not UI. UI should use the section hooks and the store.
 */
const useWeddingContextSummary = (): {
  summary: WeddingContextSummary | undefined;
  // The checklist the task lookup reads. Null while loading.
  checklist: ResolvedChecklist | null;
  ready: boolean;
  failed: boolean;
} => {
  const weddingId = useSelector(getWeddingId) || undefined;
  const writer = useSelector(selectPlannerWriter);
  const profile = useSelector(selectPlannerProfile);
  const { summary: budget, settled: budgetSettled, failed: budgetFailed } = useBudgetSummary();
  const {
    summary: checklist,
    resolved: resolvedChecklist,
    settled: checklistSettled,
    failed: checklistFailed,
  } = useChecklistSummary();
  const guests = useGuestsSummary();
  const suppliers = useSuppliersSummary();
  const events = useEventsSummary();
  const preferences = useVenuePreferencesSummary();

  const summary = useMemo(
    () =>
      weddingId
        ? { weddingId, writer, profile, budget, checklist, guests, suppliers, events, preferences }
        : undefined,
    [weddingId, writer, profile, budget, checklist, guests, suppliers, events, preferences],
  );
  const ready = Boolean(profile) && checklistSettled && budgetSettled;
  const failed = !ready && (checklistFailed || budgetFailed);

  return useMemo(
    () => ({ summary, checklist: resolvedChecklist, ready, failed }),
    [summary, resolvedChecklist, ready, failed],
  );
};

/**
 * Keeps the request's planning summary current, so the conversation does not subscribe to it.
 * Renders nothing. A wedding data change re-renders only this component and updates the refs
 * that the transport and the task lookup read. The refs are set after commit, before the
 * couple can act, so the first send has a summary. It also reports ready and failed states.
 */
export const WeddingContextSync = ({
  target,
  checklistTarget,
  onStatusChange,
}: {
  target: MutableRefObject<WeddingContextSummary | undefined>;
  checklistTarget: MutableRefObject<ResolvedChecklist | null>;
  onStatusChange: (status: { ready: boolean; failed: boolean }) => void;
}) => {
  const { summary, checklist, ready, failed } = useWeddingContextSummary();
  useEffect(() => {
    target.current = summary;
  }, [target, summary]);
  useEffect(() => {
    checklistTarget.current = checklist;
  }, [checklistTarget, checklist]);
  useEffect(() => {
    onStatusChange({ ready, failed });
  }, [onStatusChange, ready, failed]);
  return null;
};
